/**
 * A minimal icon set, inline SVG.
 *
 * No library: these are a dozen simple shapes, and an icon dependency would
 * bring thousands of paths we do not use. `currentColor` everywhere, so they
 * inherit the button colour in both themes.
 */

export type IconName =
  | 'play' | 'pause' | 'stop' | 'plus' | 'reset' | 'download'
  | 'clone' | 'report' | 'search' | 'block' | 'print' | 'compare' | 'sound'
  | 'edit' | 'trash';

const PATHS: Record<IconName, React.ReactNode> = {
  play:  <path d="M5 3.5v9l8-4.5-8-4.5z" fill="currentColor" stroke="none" />,
  pause: <><rect x="4.5" y="3.5" width="2.6" height="9" fill="currentColor" stroke="none" rx=".6" />
           <rect x="8.9" y="3.5" width="2.6" height="9" fill="currentColor" stroke="none" rx=".6" /></>,
  stop:  <rect x="4" y="4" width="8" height="8" rx="1.2" fill="currentColor" stroke="none" />,
  plus:  <path d="M8 3.5v9M3.5 8h9" />,
  reset: <><path d="M13 8a5 5 0 1 1-1.6-3.7" /><path d="M13 2.2V5h-2.8" /></>,
  download: <><path d="M8 2.8v7.4" /><path d="M5.2 7.6 8 10.4l2.8-2.8" /><path d="M3 12.6h10" /></>,
  clone: <><rect x="5.6" y="2.6" width="7.8" height="7.8" rx="1.4" />
           <path d="M10.4 13.4H4.1a1.5 1.5 0 0 1-1.5-1.5V5.6" /></>,
  report: <><path d="M2.8 13.2h10.4" /><path d="M4.8 13.2V8.4" />
            <path d="M8 13.2V3.6" /><path d="M11.2 13.2V6.6" /></>,
  search: <><circle cx="7.2" cy="7.2" r="3.9" /><path d="M10.2 10.2 13.4 13.4" /></>,
  block:  <><circle cx="8" cy="8" r="5.2" /><path d="M4.3 11.7 11.7 4.3" /></>,
  print:  <><path d="M4.4 6V2.8h7.2V6" /><rect x="2.6" y="6" width="10.8" height="4.6" rx="1.2" />
            <path d="M4.4 9.6h7.2v3.6H4.4z" /></>,
  compare: <><path d="M8 2.6v10.8" /><path d="M3.2 5.4h2.6M3.2 5.4 1.9 8.6h2.6L3.2 5.4z" />
             <path d="M12.8 5.4h-2.6M12.8 5.4 14.1 8.6h-2.6l1.3-3.2z" /></>,
  sound: <><path d="M8.4 3.4 5.2 6H3v4h2.2l3.2 2.6V3.4z" />
           <path d="M10.8 6.2a2.6 2.6 0 0 1 0 3.6" /></>,
  edit:  <><path d="M11.1 2.6a1.6 1.6 0 0 1 2.3 2.3L6 12.3l-3 .7.7-3 7.4-7.4z" />
           <path d="M10 3.7l2.3 2.3" /></>,
  trash: <><path d="M2.8 4.4h10.4" /><path d="M6.4 4.4V3.1h3.2v1.3" />
           <path d="M4.2 4.4l.6 8.2a.9.9 0 0 0 .9.8h4.6a.9.9 0 0 0 .9-.8l.6-8.2" />
           <path d="M6.7 7v3.6M9.3 7v3.6" /></>,
};

export default function Icon({ name, size = 15 }: { name: IconName; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" focusable="false"
         fill="none" stroke="currentColor" strokeWidth="1.5"
         strokeLinecap="round" strokeLinejoin="round" className="icon">
      {PATHS[name]}
    </svg>
  );
}
